/**
 * Ticket List View - Full Registry Table
 * Part of the unified Ticket Registry application
 * 
 * Features:
 * - Sortable columns
 * - Configurable visible columns
 * - Search across all fields
 * - "Show on Map" action
 * - Responsive mobile cards
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Search, Filter, SortAsc, SortDesc, Columns, Download, 
  MapPin, Eye, ChevronDown, ChevronUp, X, Ticket, 
  CheckCircle, AlertCircle, FileText, ExternalLink
} from 'lucide-react';

// Default visible columns
const DEFAULT_COLUMNS = [
  'thumbnail',
  'registry_reference',
  'location',
  'ticket_form',
  'verification',
  'grading',
  'collection',
  'actions'
];

// All available columns
const ALL_COLUMNS = {
  thumbnail: { label: 'Image', width: '60px', sortable: false },
  registry_reference: { label: 'Registry ID', width: '140px', sortable: true },
  location: { label: 'Location', width: '150px', sortable: true },
  ticket_form: { label: 'Form', width: '120px', sortable: true },
  seat_use: { label: 'Seat Use', width: '140px', sortable: true },
  verification: { label: 'Verification', width: '150px', sortable: true },
  grading: { label: 'Grading', width: '120px', sortable: true },
  collection: { label: 'Collection', width: '150px', sortable: true },
  historical_category: { label: 'Historical', width: '140px', sortable: true },
  evidence: { label: 'Evidence', width: '120px', sortable: true },
  last_sale: { label: 'Last Sale', width: '130px', sortable: true },
  actions: { label: 'Actions', width: '100px', sortable: false }
};

// Format ticket form for display
const formatTicketForm = (form) => {
  const labels = {
    'seat_numbered_full': 'Full Ticket',
    'blank_full': 'Blank Ticket',
    'used_stub': 'Ticket Stub',
    'unused_stub': 'Unused Stub',
    'other_material': 'Other'
  };
  return labels[form] || form?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || '—';
};

// Format collection label
const formatCollection = (status) => {
  const labels = {
    'principal_archive': 'Principal Archive',
    'private_collection': 'Private Collection',
    'institutional_collection': 'Institutional',
    'current_owner_not_established': 'Unestablished'
  };
  return labels[status] || status?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || '—';
};

// Format evidence confidence
const formatEvidence = (confidence) => {
  const labels = {
    'confirmed': 'Confirmed',
    'strongly_supported': 'Strong',
    'probable': 'Probable',
    'possible': 'Possible',
    'unresolved': 'Unresolved'
  };
  return labels[confidence] || confidence || '—';
};

// Table Row Component
const TicketRow = ({ ticket, columns, onShowOnMap, onViewDetails, isSelected }) => {
  const location = [
    ticket.section && `Sec ${ticket.section}`,
    ticket.row && `Row ${ticket.row}`,
    ticket.seat_number && `Seat ${ticket.seat_number}`
  ].filter(Boolean).join(' · ') || '—';

  const verifications = [];
  if (ticket.verification_psa_graded) verifications.push('PSA');
  if (ticket.verification_physical_inspection) verifications.push('Inspected');
  if (ticket.verification_provenance_documented) verifications.push('Provenance');
  
  return (
    <tr 
      className={`ticket-list-row ${isSelected ? 'ticket-list-row--selected' : ''}`}
      data-testid={`ticket-row-${ticket.registry_reference}`}
    >
      {columns.includes('thumbnail') && (
        <td className="ticket-list-cell ticket-list-cell--thumb">
          <div className="ticket-thumb">
            {ticket.thumbnail_url ? (
              <img src={ticket.thumbnail_url} alt="" />
            ) : (
              <div className="ticket-thumb__placeholder">
                <Ticket size={20} />
                <span className="ticket-thumb__label">No image</span>
              </div>
            )}
          </div>
        </td>
      )}
      
      {columns.includes('registry_reference') && (
        <td className="ticket-list-cell ticket-list-cell--id">
          <span className="ticket-id">{ticket.registry_reference || '—'}</span>
        </td>
      )}
      
      {columns.includes('location') && (
        <td className="ticket-list-cell">{location}</td>
      )}
      
      {columns.includes('ticket_form') && (
        <td className="ticket-list-cell">
          <span className={`ticket-form-badge ticket-form-badge--${ticket.ticket_form || 'unknown'}`}>
            {formatTicketForm(ticket.ticket_form)}
          </span>
        </td>
      )}
      
      {columns.includes('seat_use') && (
        <td className="ticket-list-cell">
          {ticket.seat_use_status === 'audience_used' ? 'Audience' : 
           ticket.seat_use_status === 'printed_not_used' ? 'Not Used' :
           ticket.seat_use_status || '—'}
        </td>
      )}
      
      {columns.includes('verification') && (
        <td className="ticket-list-cell">
          {verifications.length > 0 ? (
            <div className="verification-badges">
              {verifications.map(v => (
                <span key={v} className="verification-badge">{v}</span>
              ))}
            </div>
          ) : '—'}
        </td>
      )}
      
      {columns.includes('grading') && (
        <td className="ticket-list-cell">
          {ticket.grading_summary || '—'}
        </td>
      )}
      
      {columns.includes('collection') && (
        <td className="ticket-list-cell">
          {ticket.public_collection_label || formatCollection(ticket.ownership_status)}
        </td>
      )}
      
      {columns.includes('historical_category') && (
        <td className="ticket-list-cell">
          {ticket.historical_classifications?.join(', ') || '—'}
        </td>
      )}
      
      {columns.includes('evidence') && (
        <td className="ticket-list-cell">
          <span className={`evidence-badge evidence-badge--${ticket.public_status || 'unknown'}`}>
            {formatEvidence(ticket.public_status)}
          </span>
        </td>
      )}
      
      {columns.includes('last_sale') && (
        <td className="ticket-list-cell">
          {ticket.last_sale_date || '—'}
        </td>
      )}
      
      {columns.includes('actions') && (
        <td className="ticket-list-cell ticket-list-cell--actions">
          <button 
            type="button" 
            className="ticket-action-btn"
            onClick={() => onViewDetails(ticket)}
            title="View Record"
          >
            <Eye size={16} />
            <span className="sr-only">View Record</span>
          </button>
          {ticket.has_published_geometry ? (
            <button 
              type="button" 
              className="ticket-action-btn"
              onClick={() => onShowOnMap(ticket)}
              title="Show on Map"
            >
              <MapPin size={16} />
              <span className="sr-only">Show on Map</span>
            </button>
          ) : ticket.section ? (
            <button 
              type="button" 
              className="ticket-action-btn ticket-action-btn--disabled"
              disabled
              title="This ticket has not yet been mapped to published seating geometry"
            >
              <MapPin size={16} />
              <span className="sr-only">Not yet mapped</span>
            </button>
          ) : null}
        </td>
      )}
    </tr>
  );
};

// Mobile Card Component
const TicketCard = ({ ticket, onShowOnMap, onViewDetails }) => {
  const location = [
    ticket.section && `Sec ${ticket.section}`,
    ticket.row && `Row ${ticket.row}`,
    ticket.seat_number && `Seat ${ticket.seat_number}`
  ].filter(Boolean).join(' · ') || 'Unassigned';

  return (
    <div className="ticket-card-mobile" data-testid={`ticket-card-${ticket.registry_reference}`}>
      <div className="ticket-card-mobile__header">
        <div className="ticket-card-mobile__thumb">
          {ticket.thumbnail_url ? (
            <img src={ticket.thumbnail_url} alt="" />
          ) : (
            <div className="ticket-card-mobile__thumb-placeholder">
              <Ticket size={24} />
              <span>No image</span>
            </div>
          )}
        </div>
        <div className="ticket-card-mobile__info">
          <div className="ticket-card-mobile__id">{ticket.registry_reference}</div>
          <div className="ticket-card-mobile__location">{location}</div>
        </div>
        <span className={`ticket-form-badge ticket-form-badge--${ticket.ticket_form || 'unknown'}`}>
          {formatTicketForm(ticket.ticket_form)}
        </span>
      </div>
      
      <div className="ticket-card-mobile__details">
        {ticket.grading_summary && (
          <div className="ticket-card-mobile__detail">
            <span className="detail-label">Grading</span>
            <span className="detail-value">{ticket.grading_summary}</span>
          </div>
        )}
        <div className="ticket-card-mobile__detail">
          <span className="detail-label">Collection</span>
          <span className="detail-value">{ticket.public_collection_label || formatCollection(ticket.ownership_status)}</span>
        </div>
        <div className="ticket-card-mobile__detail">
          <span className="detail-label">Status</span>
          <span className={`evidence-badge evidence-badge--${ticket.public_status || 'unknown'}`}>
            {formatEvidence(ticket.public_status)}
          </span>
        </div>
      </div>
      
      <div className="ticket-card-mobile__actions">
        <button 
          type="button" 
          className="ticket-card-mobile__btn"
          onClick={() => onViewDetails(ticket)}
        >
          <Eye size={16} />
          <span>View Record</span>
        </button>
        {ticket.has_published_geometry ? (
          <button 
            type="button" 
            className="ticket-card-mobile__btn ticket-card-mobile__btn--map"
            onClick={() => onShowOnMap(ticket)}
          >
            <MapPin size={16} />
            <span>Show on Map</span>
          </button>
        ) : ticket.section ? (
          <span className="ticket-card-mobile__unmapped">
            Not yet mapped
          </span>
        ) : null}
      </div>
    </div>
  );
};

// Column Selector Dropdown
const ColumnSelector = ({ columns, visibleColumns, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  const toggleColumn = (col) => {
    if (visibleColumns.includes(col)) {
      onChange(visibleColumns.filter(c => c !== col));
    } else {
      onChange([...visibleColumns, col]);
    }
  };
  
  return (
    <div className="column-selector">
      <button 
        type="button" 
        className="column-selector__trigger"
        onClick={() => setIsOpen(!isOpen)}
      >
        <Columns size={18} />
        <span>Columns</span>
        <ChevronDown size={16} />
      </button>
      
      {isOpen && (
        <>
          <div className="column-selector__backdrop" onClick={() => setIsOpen(false)} />
          <div className="column-selector__dropdown">
            <div className="column-selector__header">
              <span>Visible Columns</span>
              <button type="button" onClick={() => setIsOpen(false)}>
                <X size={16} />
              </button>
            </div>
            <div className="column-selector__list">
              {Object.entries(columns).map(([key, col]) => (
                <label key={key} className="column-selector__item">
                  <input
                    type="checkbox"
                    checked={visibleColumns.includes(key)}
                    onChange={() => toggleColumn(key)}
                  />
                  <span>{col.label}</span>
                </label>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// Main List View Component
const TicketListView = ({ 
  tickets = [], 
  filters = {}, 
  onFilterChange,
  onNavigateToMap,
  onSelectTicket,
  selectedTicket,
  isLoading = false
}) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Initialize state from URL (authoritative) or defaults
  const getInitialState = useCallback(() => {
    const urlSort = searchParams.get('sort');
    const urlDir = searchParams.get('direction');
    const urlQ = searchParams.get('q');
    const urlPage = searchParams.get('page');
    const urlPageSize = searchParams.get('page_size');
    const urlColumns = searchParams.get('columns');
    
    // For columns, use URL > localStorage > default
    let columns = DEFAULT_COLUMNS;
    if (urlColumns) {
      columns = urlColumns.split(',').filter(c => ALL_COLUMNS[c]);
    } else {
      const stored = localStorage.getItem('registry-columns');
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          if (Array.isArray(parsed)) columns = parsed.filter(c => ALL_COLUMNS[c]);
        } catch (e) {}
      }
    }
    
    return {
      sortColumn: urlSort && ALL_COLUMNS[urlSort] ? urlSort : 'registry_reference',
      sortDirection: urlDir === 'desc' ? 'desc' : 'asc',
      searchQuery: urlQ || '',
      currentPage: parseInt(urlPage, 10) || 1,
      pageSize: parseInt(urlPageSize, 10) || 50,
      visibleColumns: columns
    };
  }, [searchParams]);
  
  const initialState = getInitialState();
  const [searchQuery, setSearchQuery] = useState(initialState.searchQuery);
  const [sortColumn, setSortColumn] = useState(initialState.sortColumn);
  const [sortDirection, setSortDirection] = useState(initialState.sortDirection);
  const [visibleColumns, setVisibleColumns] = useState(initialState.visibleColumns);
  const [currentPage, setCurrentPage] = useState(initialState.currentPage);
  const [pageSize, setPageSize] = useState(initialState.pageSize);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 900);
  
  // Sync state changes to URL
  const updateURLState = useCallback((updates) => {
    const newParams = new URLSearchParams(searchParams);
    
    Object.entries(updates).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '' || 
          (key === 'page' && value === 1) ||
          (key === 'page_size' && value === 50) ||
          (key === 'sort' && value === 'registry_reference') ||
          (key === 'direction' && value === 'asc')) {
        newParams.delete(key);
      } else {
        newParams.set(key, value);
      }
    });
    
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams]);
  
  // Handle resize
  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 900);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Save columns to localStorage when changed
  useEffect(() => {
    localStorage.setItem('registry-columns', JSON.stringify(visibleColumns));
    // Also update URL if not default
    const isDefault = JSON.stringify(visibleColumns.sort()) === JSON.stringify(DEFAULT_COLUMNS.sort());
    updateURLState({ columns: isDefault ? null : visibleColumns.join(',') });
  }, [visibleColumns]);
  
  // Filter and sort tickets
  const filteredTickets = useMemo(() => {
    let result = [...tickets];
    
    // Apply search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(t => 
        t.registry_reference?.toLowerCase().includes(query) ||
        t.section?.toLowerCase().includes(query) ||
        t.public_title?.toLowerCase().includes(query)
      );
    }
    
    // Apply sorting
    result.sort((a, b) => {
      let aVal = a[sortColumn] || '';
      let bVal = b[sortColumn] || '';
      
      if (sortColumn === 'location') {
        aVal = `${a.section || ''}-${a.row || ''}-${a.seat_number || ''}`;
        bVal = `${b.section || ''}-${b.row || ''}-${b.seat_number || ''}`;
      }
      
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();
      
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    
    return result;
  }, [tickets, searchQuery, sortColumn, sortDirection]);
  
  // Paginated tickets
  const paginatedTickets = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredTickets.slice(start, start + pageSize);
  }, [filteredTickets, currentPage, pageSize]);
  
  const totalPages = Math.ceil(filteredTickets.length / pageSize);
  
  // Handle sort with URL sync
  const handleSort = useCallback((column) => {
    if (!ALL_COLUMNS[column]?.sortable) return;
    
    const newDirection = sortColumn === column ? (sortDirection === 'asc' ? 'desc' : 'asc') : 'asc';
    setSortColumn(column);
    setSortDirection(newDirection);
    setCurrentPage(1); // Reset to first page on sort change
    
    updateURLState({
      sort: column,
      direction: newDirection,
      page: null // Reset page
    });
  }, [sortColumn, sortDirection, updateURLState]);
  
  // Handle search with URL sync
  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
    setCurrentPage(1);
    updateURLState({ q: query || null, page: null });
  }, [updateURLState]);
  
  // Handle page change with URL sync
  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
    updateURLState({ page: page > 1 ? page : null });
  }, [updateURLState]);
  
  // Handle show on map
  const handleShowOnMap = useCallback((ticket) => {
    // Build map URL with ticket's section
    const params = new URLSearchParams(searchParams);
    if (ticket.section) {
      params.set('zone', ticket.section.toLowerCase());
    }
    params.set('ticket', ticket.registry_reference);
    navigate(`/ticket-registry/map?${params.toString()}`);
  }, [navigate, searchParams]);
  
  // Handle view details
  const handleViewDetails = useCallback((ticket) => {
    const ref = ticket.registry_reference;
    // Preserve current state in URL for restoration
    const currentState = new URLSearchParams(searchParams);
    currentState.set('return_page', currentPage.toString());
    if (sortColumn !== 'registry_reference') currentState.set('return_sort', sortColumn);
    if (sortDirection !== 'asc') currentState.set('return_direction', sortDirection);
    if (searchQuery) currentState.set('return_q', searchQuery);
    
    navigate(`/ticket-registry/tickets/${encodeURIComponent(ref)}?${currentState.toString()}`);
  }, [navigate, searchParams, currentPage, sortColumn, sortDirection, searchQuery]);
  
  return (
    <div className="ticket-list-view" data-testid="ticket-list-view">
      {/* Toolbar */}
      <div className="ticket-list-toolbar">
        <div className="ticket-list-toolbar__search">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search tickets..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            data-testid="ticket-search-input"
          />
          {searchQuery && (
            <button type="button" onClick={() => handleSearch('')}>
              <X size={16} />
            </button>
          )}
        </div>
        
        <div className="ticket-list-toolbar__actions">
          <ColumnSelector 
            columns={ALL_COLUMNS}
            visibleColumns={visibleColumns}
            onChange={setVisibleColumns}
          />
          
          <button type="button" className="ticket-list-toolbar__btn" title="Export">
            <Download size={18} />
            <span>Export</span>
          </button>
        </div>
        
        <div className="ticket-list-toolbar__count">
          <span className="count-number">{filteredTickets.length}</span>
          <span className="count-label">tickets</span>
        </div>
      </div>
      
      {/* Desktop Table */}
      {!isMobile && (
        <div className="ticket-list-table-wrapper">
          <table className="ticket-list-table" data-testid="ticket-list-table">
            <thead>
              <tr>
                {visibleColumns.map(col => (
                  <th 
                    key={col}
                    className={`ticket-list-header ${ALL_COLUMNS[col]?.sortable ? 'ticket-list-header--sortable' : ''}`}
                    style={{ width: ALL_COLUMNS[col]?.width }}
                    onClick={() => handleSort(col)}
                  >
                    <span>{ALL_COLUMNS[col]?.label}</span>
                    {ALL_COLUMNS[col]?.sortable && sortColumn === col && (
                      sortDirection === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paginatedTickets.map((ticket, idx) => (
                <TicketRow
                  key={ticket.registry_reference || idx}
                  ticket={ticket}
                  columns={visibleColumns}
                  onShowOnMap={handleShowOnMap}
                  onViewDetails={handleViewDetails}
                  isSelected={selectedTicket?.registry_reference === ticket.registry_reference}
                />
              ))}
            </tbody>
          </table>
          
          {filteredTickets.length === 0 && (
            <div className="ticket-list-empty">
              <Ticket size={48} strokeWidth={1} />
              <p>No tickets match the current filters</p>
            </div>
          )}
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="ticket-list-pagination" data-testid="ticket-pagination">
              <button 
                type="button" 
                className="pagination-btn"
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
              >
                First
              </button>
              <button 
                type="button" 
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              <span className="pagination-info">
                Page {currentPage} of {totalPages}
              </span>
              <button 
                type="button" 
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
              <button 
                type="button" 
                className="pagination-btn"
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
              >
                Last
              </button>
            </div>
          )}
        </div>
      )}
      
      {/* Mobile Cards */}
      {isMobile && (
        <div className="ticket-list-cards" data-testid="ticket-list-cards">
          {paginatedTickets.map((ticket, idx) => (
            <TicketCard
              key={ticket.registry_reference || idx}
              ticket={ticket}
              onShowOnMap={handleShowOnMap}
              onViewDetails={handleViewDetails}
            />
          ))}
          
          {filteredTickets.length === 0 && (
            <div className="ticket-list-empty">
              <Ticket size={48} strokeWidth={1} />
              <p>No tickets match the current filters</p>
            </div>
          )}
          
          {/* Mobile Pagination */}
          {totalPages > 1 && (
            <div className="ticket-list-pagination ticket-list-pagination--mobile" data-testid="ticket-pagination-mobile">
              <button 
                type="button" 
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              <span className="pagination-info">
                {currentPage} / {totalPages}
              </span>
              <button 
                type="button" 
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TicketListView;
